// src/app/storage.ts — Fase 5: migración v4→v5 + trainer + misiones + objetos
import { GAME_CONFIG, TRAINER_CONFIG } from '../data/gameConfig.js';
import type { PlayerState, PlayerOptions } from '../entities/player.js';
import { createInitialMissions } from '../data/missions.js';

const SAVE_KEY = GAME_CONFIG.storageKey;
const OPTIONS_KEY = GAME_CONFIG.optionsKey;
const LEGACY_KEYS = ['aetheria:save:v1','aetheria:save:v2','aetheria:save:v3','aetheria:save:v4'];

export function hasSave(): boolean {
  try { return !!localStorage.getItem(SAVE_KEY) || LEGACY_KEYS.some(k=> !!localStorage.getItem(k)); } catch { return false; }
}

export function loadSave(): PlayerState | null {
  try{
    let raw = localStorage.getItem(SAVE_KEY);
    if(!raw){ for(const k of LEGACY_KEYS){ raw=localStorage.getItem(k); if(raw) break; } if(!raw) return null; }
    const parsed = JSON.parse(raw) as any;
    const curVer = GAME_CONFIG.saveVersion;
    if((parsed.meta.saveVersion ?? 1) < curVer){
      console.info('[Aetheria] Migrando save', parsed.meta.saveVersion, '→', curVer);
      if(!parsed.world.zoneId) parsed.world.zoneId = GAME_CONFIG.defaultZone;
      if(!parsed.world.discoveredZones) parsed.world.discoveredZones = [GAME_CONFIG.defaultZone];
      if(!parsed.world.flags) parsed.world.flags = {};
      if(!parsed.registry) parsed.registry = {};
      if(!parsed.team) parsed.team = { asterias:[], boxed:[] };
      else {
        if(!parsed.team.boxed) parsed.team.boxed=[];
        for(const inst of [...parsed.team.asterias, ...parsed.team.boxed]){
          if(inst.hp===undefined) inst.hp = 20;
          if(inst.maxHp===undefined) inst.maxHp = 20;
          if(inst.bond===undefined) inst.bond = 10;
          if(inst.energy===undefined) inst.energy = 8;
          if(inst.maxEnergy===undefined) inst.maxEnergy = 8;
          if(inst.xp===undefined) inst.xp = 0;
          if(inst.xpToNext===undefined) inst.xpToNext = 20;
          if(!inst.moves) inst.moves = [];
          if(!inst.statStages) inst.statStages = { atk:0, def:0, spd:0, acc:0 };
          if(inst.status===undefined) inst.status = null;
          if(!inst.abilityId) inst.abilityId = 'fotosintesis';
        }
      }
      if(!parsed.inventory) parsed.inventory = [];
      if(!parsed.inventory.find((i:any)=>i.id==='nucleo_vinculo')) parsed.inventory.unshift({id:'nucleo_vinculo', qty:12});
      if(!parsed.inventory.find((i:any)=>i.id==='pocion_aurea')) parsed.inventory.push({id:'pocion_aurea', qty:5});
      if(parsed.currency===undefined) parsed.currency = 120;
      if(!parsed.trainer){
        parsed.trainer = { level:1, xp:0, xpToNext: TRAINER_CONFIG.baseXpToLevel, aureos: parsed.currency ?? 120 };
      }
      if(parsed.trainer && parsed.trainer.aureos===undefined) parsed.trainer.aureos = parsed.currency ?? 120;
      if(!parsed.collectedObjects) parsed.collectedObjects = [];
      if(!parsed.missions || !Array.isArray(parsed.missions) || parsed.missions.length===0){
        parsed.missions = createInitialMissions();
      } else {
        // asegurar misiones nuevas existan
        const existingIds = new Set(parsed.missions.map((m:any)=> m.id));
        for(const def of createInitialMissions()){
          if(!existingIds.has(def.id)) parsed.missions.push(def);
        }
      }
      if(!parsed.stats) parsed.stats = { playtimeSeconds:0, stepsWalked:0, asteriasCaptured:0, battlesWon:0, encounters:0, missionsCompleted:0, objectsCollected:0 };
      if(parsed.stats.missionsCompleted===undefined) parsed.stats.missionsCompleted=0;
      if(parsed.stats.objectsCollected===undefined) parsed.stats.objectsCollected=0;
      // migrar quests viejos a misiones si existían? no necesario
      parsed.meta.saveVersion = curVer;
      try{ localStorage.setItem(SAVE_KEY, JSON.stringify(parsed)); }catch{}
    }
    if(!parsed.registry) parsed.registry = {};
    if(!parsed.world.zoneId) parsed.world.zoneId = GAME_CONFIG.defaultZone;
    if(!parsed.world.discoveredZones) parsed.world.discoveredZones=[GAME_CONFIG.defaultZone];
    if(!parsed.world.flags) parsed.world.flags={};
    if(!parsed.time) parsed.time={tick:0.25, phase:'dia'};
    if(!parsed.stats) parsed.stats={ playtimeSeconds:0, stepsWalked:0, asteriasCaptured:0, battlesWon:0, encounters:0, missionsCompleted:0, objectsCollected:0 };
    if(parsed.currency===undefined) parsed.currency=120;
    if(!parsed.trainer) parsed.trainer={ level:1, xp:0, xpToNext: TRAINER_CONFIG.baseXpToLevel, aureos: parsed.currency };
    if(parsed.trainer.aureos===undefined) parsed.trainer.aureos = parsed.currency;
    if(parsed.trainer.xp===undefined) parsed.trainer.xp=0;
    if(parsed.trainer.xpToNext===undefined) parsed.trainer.xpToNext = TRAINER_CONFIG.baseXpToLevel;
    if(!parsed.collectedObjects) parsed.collectedObjects=[];
    if(!parsed.missions) parsed.missions = createInitialMissions();
    if(!parsed.inventory) parsed.inventory=[];
    if(!parsed.inventory.find((i:any)=>i.id==='nucleo_vinculo')) parsed.inventory.unshift({id:'nucleo_vinculo', qty:12});
    if(!parsed.inventory.find((i:any)=>i.id==='pocion_aurea')) parsed.inventory.push({id:'pocion_aurea', qty:5});
    // sync currency <-> trainer.aureos
    parsed.currency = parsed.trainer.aureos;
    for(const inst of [...parsed.team.asterias, ...parsed.team.boxed]){
      if(inst.energy===undefined) inst.energy = inst.maxEnergy ?? 8;
      if(inst.maxEnergy===undefined) inst.maxEnergy = 8;
      if(inst.xp===undefined) inst.xp=0;
      if(inst.xpToNext===undefined) inst.xpToNext = Math.floor(20*Math.pow(1.35, (inst.level||1)-1));
      if(!inst.moves || inst.moves.length===0) inst.moves = [];
      if(!inst.statStages) inst.statStages={ atk:0, def:0, spd:0, acc:0 };
      if(inst.status===undefined) inst.status=null;
    }
    return parsed as PlayerState;
  }catch(e){ console.error('[Aetheria] loadSave failed',e); return null; }
}

export function saveLocal(state: PlayerState): void {
  try{
    // sincronizar currency
    if(state.trainer) state.currency = state.trainer.aureos;
    state.meta.updatedAt=new Date().toISOString();
    localStorage.setItem(SAVE_KEY, JSON.stringify(state));
  }catch(e){ console.error('[Aetheria] saveLocal failed',e); }
}
export function clearSave(): void {
  try{ localStorage.removeItem(SAVE_KEY); for(const k of LEGACY_KEYS) localStorage.removeItem(k); }catch{}
}
export function loadOptions(): PlayerOptions | null {
  try{ const raw=localStorage.getItem(OPTIONS_KEY); return raw? JSON.parse(raw) as PlayerOptions : null; }catch{ return null; }
}
export function saveOptions(opts: PlayerOptions): void{ try{ localStorage.setItem(OPTIONS_KEY, JSON.stringify(opts)); }catch{} }
export async function saveRemote(_state: PlayerState): Promise<{ok:boolean;error?:string}>{
  try{
    const res=await fetch('/api/save',{ method:'POST', headers:{'content-type':'application/json'}, body: JSON.stringify({ intent:'save', at:new Date().toISOString(), zone: (_state.world as any).zoneId, pos:_state.world.position, team: _state.team.asterias.length, currency:_state.currency, trainerLevel:_state.trainer.level }) });
    if(!res.ok) return { ok:false, error:`${res.status} ${res.statusText}` };
    return { ok:true };
  }catch(e){ return { ok:false, error:String(e)}}
}
